import React, { useState, useEffect, useMemo } from 'react';
import { Users, Edit, X, Loader2, Send, UserX, Download } from 'lucide-react';
import { GenericListView, FilterConfig, DisciplinePill, getDisciplinePillClassName, getGroupConfig, StatusPill, getStatusPillClassName, ProviderAvatar, ConfirmDialog, useToast } from '../common';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchUserById,
  resendUserInvite,
  revokeUserInvite,
  downloadAccessReview,
} from '../../services/admin';
import { UserData, UserFormData } from '../../types/admin/user';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useAuth } from '../../context/authcontext';
import { PROVIDER_TYPES } from '../../dtos/common/discipline.dto';
import { getProviderTypeEntry } from '../../constants/disciplines';
import { isApiError } from '../../dtos/common/api.dto';
import { describeApiError } from '../../utils/apiErrors';
import { formatInAgencyTz, toAgencyDate } from '../../utils/agencyTime';
import { getLicenseBadge, isInvitePending, isRevoked, INVITE_PENDING_BADGE, REVOKED_BADGE, UserBadge } from './userBadges';
import { canSignBaa } from '../../utils/baaAccess';
import { SETUP_STATUS_UPDATED_EVENT } from '../../constants/navigationEvents';

/** Roles that need a clinical provider type (backend: 400 PROVIDER_TYPE_REQUIRED) */
const CLINICAL_ROLES = ['clinician', 'clinicalmgr'];
export const PROVIDER_TYPE_REQUIRED_MESSAGE = 'Clinician and Clinical Manager users need a provider type (RN, LVN, PT, OT, ST, MSW or HHA).';

/**
 * Provider type for a stored or typed value. An exact registry code (or alias: LPN, OTA, SLP, CNA)
 * wins, so "PTA" stays an assistant and is never read as "PT"; legacy free text is matched by
 * keyword ("Registered nurse" → RN).
 */
export function providerTypeFromText(value: string): string | null {
  const exact = getProviderTypeEntry(value);
  if (exact) return exact.code;
  const t = value.trim().toUpperCase();
  if (t.includes('ASSIST')) return null;
  if (t.includes('NURS')) return 'RN';
  if (t.includes('PHYSICAL')) return 'PT';
  if (t.includes('OCCUPATIONAL')) return 'OT';
  if (t.includes('SPEECH')) return 'ST';
  if (t.includes('SOCIAL')) return 'MSW';
  if (t.includes('AIDE')) return 'HHA';
  return null;
}

/** True when the roles need a provider type and none of PROVIDER_TYPES is selected */
export function isMissingClinicalProviderType(data: Pick<UserFormData, 'groups' | 'isProvider' | 'providerType'>): boolean {
  const needsType = data.groups.some((group) => CLINICAL_ROLES.includes(group.toLowerCase()));
  if (!needsType) return false;
  const type = (data.providerType || '').toUpperCase();
  return !data.isProvider || !(PROVIDER_TYPES as readonly string[]).includes(type);
}

/** Message for a failed invite action; 409 INVITE_NOT_PENDING has its own copy */
function inviteErrorMessage(err: unknown, fallback: string): string {
  if (isApiError(err) && (err.domainCode === 'INVITE_NOT_PENDING' || err.statusCode === 409)) {
    return 'This invite is no longer pending. The user has already signed in or the invite was revoked.';
  }
  return describeApiError(err, fallback);
}

interface UserManagementProps {
  expanded?: boolean;
  toggleExpanded?: () => void;
}

const UserManagement = ({ expanded = true, toggleExpanded }: UserManagementProps) => {
  // State for users
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('All');
  const [filterProviderType, setFilterProviderType] = useState('All');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const { user: authUser, agencyTimezone } = useAuth();
  const timezone = agencyTimezone || 'America/Chicago';
  const { showToast } = useToast();
  const [revokeTarget, setRevokeTarget] = useState<UserData | null>(null);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const [downloadingReview, setDownloadingReview] = useState(false);
  // Owner/admin: roles live in the DB (cognito:groups is empty), so use the BAA signer check
  const canDownloadAccessReview = canSignBaa(authUser);
  const today = toAgencyDate(new Date(), timezone);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(6);
  const [isSaving, setIsSaving] = useState(false);
  const [savingMessage, setSavingMessage] = useState('');

  // Modal state
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  /** Save failure shown in the modal (the modal stays open so the form is not lost) */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [providerTypeError, setProviderTypeError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    groups: ['user'],
    isProvider: false,
    providerType: 'not_a_provider',
    licenseNumber: '',
    licenseExpiry: '',
    npi: '',
    phone: '',
    status: 'Active'
  });

  // Permission checks for user management
  const canReadUsersPii = useCanPerform('read:users:pii');
  const canCreateUsersPii = useCanPerform('create:users:pii');
  const canUpdateUsersPii = useCanPerform('update:users:pii');
  // Invite resend/revoke need admin:users:nonpii:update
  const canManageInvites = useCanPerform('update:users:nonpii');

  // User can manage (create/update) if they have either create or update permission
  const canManageUsers = canCreateUsersPii || canUpdateUsersPii;

  // User has read-only access if they have read permission but can't create or update
  const isReadOnlyMode = canReadUsersPii && !canManageUsers;

  // Canonical provider discipline types (all disciplines that can be assigned to a clinical user)
  // Wrapped in useMemo to prevent unnecessary re-renders when used in dependency arrays
  const providerTypes = useMemo<string[]>(() => [...PROVIDER_TYPES, 'not_a_provider'], []);
  const userGroups = useMemo(() => ['user', 'owner', 'admin', 'clinician', 'clinicalmgr', 'qa', 'billing', 'coordinator', 'scheduler', 'readonly'], []);

  // Fetch users from API
  const loadUsers = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    setLoadFailed(false);
    try {
      const { users, totalCount } = await fetchUsers(
        currentPage,
        pageSize,
        searchTerm || undefined,
        filterGroup === 'All' ? undefined : filterGroup,
        filterProviderType === 'All' ? undefined : filterProviderType
      );
      setUsers(users);
      setFilteredUsers(users);
      setTotalCount(totalCount);
      setHasLoadedOnce(true);
    } catch (err) {
      console.error('Failed to load users:', err instanceof Error ? err.message : 'unknown error');
      // Error state: the banner says "Couldn't load users." with a Retry button; no demo data, no empty-state message
      setLoadFailed(true);
      setUsers([]);
      setFilteredUsers([]);
      setTotalCount(0);
      setHasLoadedOnce(false);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  // Refresh handler for GenericListView
  const handleRefresh = () => {
    loadUsers(true);
  };

  // Load users on initial render and when filters change
  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]); // Only depend on pagination parameters

  // Apply client-side filtering when search/filters change
  // This provides immediate feedback without waiting for API
  // A debounced API call would be better for production
  useEffect(() => {
    // If we're not already loading data from the API, apply client-side filtering
    // In a production app, this would be replaced with a debounced API call
    if (!isLoading) {
      const delayDebounceFn = setTimeout(() => {
        loadUsers();
      }, 500); // 500ms debounce

      return () => clearTimeout(delayDebounceFn);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterGroup, filterProviderType]);

  // Fetch user details when modal opens with a provider user
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (showUserModal && currentUser?.id && currentUser?.isProvider) {
        setIsLoadingDetails(true);
        setModalError(null);
        try {
          const userWithPII = await fetchUserById(currentUser.id);

          setFormData({
            ...toFormData(currentUser),
            licenseNumber: userWithPII.licenseNumber || '',
            licenseExpiry: userWithPII.licenseExpiry || '',
            npi: userWithPII.npi || '',
            phone: userWithPII.phone || '',
            providerType: userWithPII.providerType || currentUser.providerType,
            status: userWithPII.status || currentUser.status || 'Active'
          });
        } catch (err) {
          console.error('Failed to fetch user details:', err instanceof Error ? err.message : 'unknown error');
          setModalError('Failed to load complete provider information.');
          // Still set the form data with what we have
          setFormData(toFormData(currentUser));
        } finally {
          setIsLoadingDetails(false);
        }
      } else if (showUserModal && currentUser) {
        // Non-provider user or new user
        setFormData(toFormData(currentUser));
      } else if (showUserModal) {
        // New user
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          groups: ['user'],
          isProvider: false,
          providerType: 'not_a_provider',
          licenseNumber: '',
          licenseExpiry: '',
          npi: '',
          phone: '',
          status: 'Active'
        });
      }
    };

    if (showUserModal) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUserModal, currentUser?.id, currentUser?.isProvider, currentUser?.providerType, currentUser?.status]); // currentUser intentionally excluded - only using specific properties to avoid unnecessary re-renders

  /** Map a list/detail user to the form model */
  const toFormData = (u: UserData): UserFormData => ({
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
    groups: u.groups,
    isProvider: !!u.providerType && u.providerType !== 'not_a_provider',
    providerType: u.providerType || 'not_a_provider',
    licenseNumber: u.licenseNumber || '',
    licenseExpiry: u.licenseExpiry || '',
    npi: u.npi || '',
    phone: u.phone || '',
    status: u.status || 'Active',
  });

  // Helper function to get discipline badge for table display. Phase 20A: a registry provider type
  // (LVN, PTA, COTA, SLPA, …) shows as itself; free text falls back to keyword matching.
  const getDisciplineBadge = (providerType: string | undefined | null): string => {
    if (!providerType) return '';
    return providerTypeFromText(providerType) || providerType;
  };

  // Helper function to normalize provider type for backend (a registry provider type or not_a_provider)
  const normalizeProviderTypeForBackend = (providerType: string): string => {
    if (providerType === 'not_a_provider') {
      return providerType;
    }
    return providerTypeFromText(providerType) || providerType;
  };

  // CRUD operations
  const addUser = async (userData: UserFormData) => {
    setIsSaving(true);
    setSavingMessage('Creating user...');
    setError(null);
    setSaveError(null);
    try {
      const newUser = await createUser(userData);
      setShowUserModal(false); // Close only after the save succeeds
      setUsers([...users, newUser]);
      window.dispatchEvent(new Event(SETUP_STATUS_UPDATED_EVENT));
      setSavingMessage('User created successfully!');
      setTimeout(() => {
        setIsSaving(false);
        setSavingMessage('');
      }, 2000);
      loadUsers(); // Refresh the user list from API
    } catch (err) {
      console.error('Failed to create user:', err instanceof Error ? err.message : 'unknown error');
      setSaveError(describeApiError(err, 'Failed to create user. Please try again.'));
      setIsSaving(false);
      setSavingMessage('');
    }
  };

  const handleUpdateUser = async (userId: string, userData: UserFormData) => {
    setIsSaving(true);
    setSavingMessage('Updating user...');
    setError(null);
    setSaveError(null);
    try {
      const updatedUser = await updateUser(userId, userData);
      setShowUserModal(false); // Close only after the save succeeds
      setUsers(users.map(u => (u.id === userId ? updatedUser : u)));
      setSavingMessage('User updated successfully!');
      setTimeout(() => {
        setIsSaving(false);
        setSavingMessage('');
      }, 2000);
      loadUsers(); // Refresh the user list from API
    } catch (err) {
      console.error('Failed to update user:', err instanceof Error ? err.message : 'unknown error');
      setSaveError(describeApiError(err, 'Failed to update user. Please try again.'));
      setIsSaving(false);
      setSavingMessage('');
    }
  };

  // This function is defined for future use but not currently used
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteUser = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setIsLoading(true);
      setError(null);
      try {
        await deleteUser(id);
        setUsers(users.filter(user => user.id !== id));
        loadUsers(); // Refresh the user list from API
      } catch (err) {
        console.error('Failed to delete user:', err);
        setError('Failed to delete user. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Function to open edit modal
  const openEditModal = (user: UserData) => {
    setSaveError(null);
    setProviderTypeError(null);
    setCurrentUser(user);
    setShowUserModal(true);
  };

  const handleResendInvite = async (user: UserData) => {
    setInviteBusyId(user.id);
    try {
      await resendUserInvite(user.id);
      showToast('Invite sent again.', { variant: 'success' });
    } catch (err) {
      showToast(inviteErrorMessage(err, "Couldn't resend the invite. Please try again."), { variant: 'error' });
    } finally {
      setInviteBusyId(null);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    const target = revokeTarget;
    setInviteBusyId(target.id);
    try {
      await revokeUserInvite(target.id);
      showToast('Invite revoked.', { variant: 'success' });
      loadUsers(true);
    } catch (err) {
      showToast(inviteErrorMessage(err, "Couldn't revoke the invite. Please try again."), { variant: 'error' });
    } finally {
      setRevokeTarget(null);
      setInviteBusyId(null);
    }
  };

  const handleDownloadAccessReview = async () => {
    setDownloadingReview(true);
    try {
      await downloadAccessReview();
      showToast('Access review downloaded.', { variant: 'success' });
    } catch (err) {
      showToast(describeApiError(err, "Couldn't download the access review. Please try again."), { variant: 'error' });
    } finally {
      setDownloadingReview(false);
    }
  };

  const formatLastLogin = (iso: string | null | undefined): string =>
    iso ? formatInAgencyTz(iso, timezone, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never';

  // Handler for form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

      // Handle provider type selection
      if (name === 'providerType') {
        if (value === 'not_a_provider') {
          // If not_a_provider is selected, uncheck the isProvider box
          setFormData({
            ...formData,
            [name]: value,
            isProvider: false
          });
          return;
        } else {
          // If a valid provider type is selected, ensure isProvider is checked
          setFormData({
            ...formData,
            [name]: value,
            isProvider: true
          });
          return;
        }
      }

      // If this is the provider checkbox and it's being checked
      if (name === 'isProvider') {
        if (checked) {
          setFormData(prev => ({
            ...prev,
            [name]: checked,
            providerType: prev.providerType !== 'not_a_provider' ? prev.providerType : 'RN',
            status: prev.status || 'Active'
          }));
        } else {
          // If the provider checkbox is unchecked, set provider type to not_a_provider
          setFormData(prev => ({
            ...prev,
            [name]: false,
            providerType: 'not_a_provider'
          }));
        }
      } else {
        // Normal field handling
        setFormData({
          ...formData,
          [name]: type === 'checkbox' ? checked : value
        });
      }
    };

    const handleGroupChange = (group: string) => {
      const updatedGroups = [...formData.groups];

      if (updatedGroups.includes(group)) {
        // Remove the group if it's already selected
        const index = updatedGroups.indexOf(group);
        updatedGroups.splice(index, 1);
      } else {
        // Add the group if it's not selected
        updatedGroups.push(group);
      }

      setFormData({
        ...formData,
        groups: updatedGroups
      });
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      // Don't proceed if still loading provider details
      if (isLoadingDetails) {
        alert('Please wait while provider information is loading');
        return;
      }

      // Ensure at least one group is selected
      if (formData.groups.length === 0) {
        alert('Please select at least one user group');
        return;
      }

      // Clinician / Clinical Manager need a clinical provider type (backend: PROVIDER_TYPE_REQUIRED)
      if (isMissingClinicalProviderType(formData)) {
        setProviderTypeError(PROVIDER_TYPE_REQUIRED_MESSAGE);
        return;
      }
      setProviderTypeError(null);

      // Make a copy of the form data to submit
      const dataToSubmit = { ...formData };

      // Validate provider fields if user is a provider
      if (dataToSubmit.isProvider) {
        if (!dataToSubmit.providerType || dataToSubmit.providerType === 'not_a_provider') {
          alert('Please select a valid provider type');
          return;
        }

        // Normalize provider type to standard values (RN, PT, OT, ST) before sending to backend
        dataToSubmit.providerType = normalizeProviderTypeForBackend(dataToSubmit.providerType);

        // Set default provider status if missing
        if (!dataToSubmit.status) {
          dataToSubmit.status = 'Active';
        }
      } else {
        // If not a provider, set the providerType to not_a_provider
        dataToSubmit.providerType = 'not_a_provider';
      }

      if (currentUser) {
        handleUpdateUser(currentUser.id, dataToSubmit);
      } else {
        addUser(dataToSubmit);
      }
    };

  // Inline error only while the form still lacks the provider type
  const showProviderTypeError = !!providerTypeError && isMissingClinicalProviderType(formData);

  // Render user modal JSX
  const renderUserModal = () => (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md md:max-w-2xl lg:max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex justify-between items-center border-b border-gray-200 px-4 md:px-6 py-4 flex-shrink-0">
            <h2 className="text-lg md:text-xl font-semibold text-left">
              {currentUser ? 'Edit User' : 'Add New User'}
            </h2>
            <button
              onClick={() => setShowUserModal(false)}
              className="text-gray-500 hover:text-gray-700"
              type="button"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-4 md:px-6 py-4 overflow-y-auto">
            {saveError && (
              <div className="bg-red-50 border-l-4 border-red-400 text-red-700 p-3 mb-4 rounded text-sm" role="alert" data-testid="user-save-error">
                {saveError}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                  required
                  disabled={isReadOnlyMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                  required
                  disabled={isReadOnlyMode}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                required
                disabled={isReadOnlyMode}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">User Groups (select at least one)</label>
              <div className="flex flex-wrap gap-2 mt-1">                  {userGroups.map((group, index) => (
                  <div
                    key={index}
                    onClick={() => !isReadOnlyMode && handleGroupChange(group)}
                    className={`${isReadOnlyMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} px-3 py-1 text-xs font-semibold rounded transition-all ${
                      formData.groups.includes(group)
                        ? `${getGroupConfig(group).className} ring-2 ring-offset-1 ring-blue-400`
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    {getGroupConfig(group).label}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isProvider"
                  name="isProvider"
                  checked={formData.isProvider}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isReadOnlyMode}
                />
                <label htmlFor="isProvider" className="ml-2 block text-sm font-medium text-gray-700">
                  This user is also a provider
                </label>
              </div>
              {showProviderTypeError && (
                <p className="mt-1 text-xs text-red-600 text-left" role="alert" data-testid="provider-type-error">{providerTypeError}</p>
              )}
            </div>

            {/* Provider-specific fields */}
            {formData.isProvider && (
              <div className="bg-blue-50 p-4 rounded-lg mb-4 relative">
                <h3 className="font-medium text-blue-800 mb-3 text-left flex items-center">
                  Provider Information
                  {isLoadingDetails && <Loader2 className="ml-2 animate-spin text-blue-600" size={16} />}
                </h3>

                {modalError && (
                  <div className="bg-red-50 border-l-4 border-red-400 text-red-700 p-3 mb-4 rounded text-sm">
                    <p>{modalError}</p>
                    <p className="text-xs mt-1">Some provider details might be incomplete.</p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Provider Type</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {providerTypes
                      .filter(type => type !== 'not_a_provider')
                      .map(type => {
                        const isSelected = formData.providerType === type;
                        return (
                          <button
                            key={type}
                            type="button"
                            disabled={isLoadingDetails || isReadOnlyMode}
                            onClick={() => !isLoadingDetails && !isReadOnlyMode && setFormData(prev => ({ ...prev, providerType: type }))}
                            className={`rounded transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                              isSelected
                                ? 'ring-2 ring-offset-1 ring-blue-500 shadow-sm'
                                : 'opacity-50 hover:opacity-90'
                            } ${isLoadingDetails || isReadOnlyMode ? 'cursor-not-allowed' : 'cursor-pointer'} disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            <DisciplinePill discipline={type} />
                          </button>
                        );
                      })
                    }
                  </div>
                  {/* Hidden validation anchor — shows browser validation msg if no type selected */}
                  {formData.isProvider && !formData.providerType && (
                    <p className="mt-1 text-xs text-red-600">Please select a provider type.</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                    required={formData.isProvider}
                    disabled={isLoadingDetails || isReadOnlyMode}
                    placeholder={isLoadingDetails ? "Loading..." : ""}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label htmlFor="user-licenseNumber" className="block text-sm font-medium text-gray-700 mb-1 text-left">License Number</label>
                    <input
                      id="user-licenseNumber"
                      type="text"
                      name="licenseNumber"
                      value={formData.licenseNumber || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                      required={formData.isProvider}
                      disabled={isLoadingDetails || isReadOnlyMode}
                      placeholder={isLoadingDetails ? "Loading..." : ""}
                    />
                  </div>
                  <div>
                    <label htmlFor="user-licenseExpiry" className="block text-sm font-medium text-gray-700 mb-1 text-left">License Expiry</label>
                    <input
                      id="user-licenseExpiry"
                      type="date"
                      name="licenseExpiry"
                      value={formData.licenseExpiry || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                      disabled={isLoadingDetails || isReadOnlyMode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 text-left">NPI Number</label>
                    <input
                      type="text"
                      name="npi"
                      value={formData.npi || ''}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                      required={formData.isProvider}
                      disabled={isLoadingDetails || isReadOnlyMode}
                      placeholder={isLoadingDetails ? "Loading..." : ""}
                    />
                  </div>
                </div>

                {currentUser?.clinicianCode && (
                  <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <span className="font-medium">HHAX clinician code</span>
                    <code className="rounded bg-gray-100 px-2 py-1 font-mono">{currentUser.clinicianCode}</code>
                    <button
                      type="button"
                      className="min-h-[36px] rounded-md border border-gray-300 px-2 text-xs hover:bg-gray-50"
                      onClick={() => { navigator.clipboard?.writeText(currentUser.clinicianCode || '').catch(() => undefined); }}
                    >
                      Copy
                    </button>
                    <span className="text-xs text-gray-500">Enter as the 3rd Party Caregiver Code in HHAeXchange.</span>
                  </div>
                )}

                {isLoadingDetails && (
                  <div className="absolute inset-0 bg-blue-50 bg-opacity-50 flex items-center justify-center rounded-lg">
                    <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
                      <Loader2 className="animate-spin text-blue-600 mr-2" size={20} />
                      <span className="text-sm font-medium">Loading provider information...</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Status</label>
              <select
                name="status"
                value={(formData.status || 'ACTIVE').toUpperCase()}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-base disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                required
                disabled={isReadOnlyMode}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
            </div>
            <div className="border-t border-gray-200 px-4 md:px-6 py-4 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowUserModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoadingDetails || isReadOnlyMode || isSaving}
              >
                {(isLoadingDetails || isSaving) && <Loader2 className="animate-spin mr-2" size={16} />}
                {currentUser ? 'Update' : 'Add'} User
              </button>
            </div>
          </form>
        </div>
      </div>
  );

  // Status badge color now handled by StatusPill component

  // Saving progress banner
  const savingBanner = isSaving ? (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
      <div className="flex items-center">
        <Loader2 className="animate-spin text-blue-600 mr-3" size={20} />
        <p className="text-sm font-medium text-blue-700">{savingMessage}</p>
      </div>
    </div>
  ) : undefined;

  // Load-failure banner: an error with Retry (no demo data)
  const loadFailureBanner = loadFailed ? (
    <div className="bg-red-50 border-red-200 border rounded-md p-3" role="alert" data-testid="users-load-error">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-red-700">Couldn&apos;t load users.</p>
        <button
          type="button"
          onClick={() => loadUsers()}
          disabled={isLoading}
          className="ml-auto rounded-md border border-red-300 bg-white px-3 py-1 text-sm text-red-800 hover:bg-red-100 disabled:opacity-50"
        >
          Retry
        </button>
      </div>
    </div>
  ) : undefined;

  /** Resend/revoke: a pending invite and admin:users:nonpii:update */
  const canInvite = (user: UserData): boolean => canManageInvites && !isRevoked(user) && isInvitePending(user);

  /** License and invite badges for a row */
  const rowBadges = (user: UserData): UserBadge[] => {
    const badges: UserBadge[] = [];
    if (isRevoked(user)) badges.push(REVOKED_BADGE);
    else if (isInvitePending(user)) badges.push(INVITE_PENDING_BADGE);
    const license = getLicenseBadge(user.licenseExpiry, today);
    if (license) badges.push(license);
    return badges;
  };

  // Build filter configurations for GenericListView
  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      key: 'group',
      label: 'Group',
      value: filterGroup,
      options: [
        { value: 'All', label: 'All Groups' },
        ...userGroups.map(group => ({ value: group, label: getGroupConfig(group).label })),
      ],
      onChange: setFilterGroup,
    },
    {
      key: 'providerType',
      label: 'Provider Type',
      value: filterProviderType,
      options: [
        { value: 'All', label: 'All Provider Types' },
        { value: 'None', label: 'Non-Providers' },
        ...providerTypes
          .filter(type => type !== 'not_a_provider')
          .map(type => ({ value: type, label: type })),
      ],
      onChange: setFilterProviderType,
    },
  ], [filterGroup, filterProviderType, userGroups, providerTypes]);

  return (
    <>
      {canDownloadAccessReview && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={handleDownloadAccessReview}
            disabled={downloadingReview}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {downloadingReview ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
            Download access review (CSV)
          </button>
        </div>
      )}
      <GenericListView
        // ListView props
        title="User Management"
        icon={<Users size={20} />}
        expanded={expanded}
        onToggleExpand={toggleExpanded}
        hideHeaderOnDesktop
        alertBanner={savingBanner ?? loadFailureBanner}
        // Data props
        items={filteredUsers}
        keyExtractor={(user) => user.id}
        columns={[
          {
            key: 'user',
            label: 'User',
            width: 'flex-1 min-w-0',
            render: (user: UserData) => (
              <div className="flex items-center gap-3">
                {/* Color-coded avatar badge */}
                {user.isProvider && user.providerType && user.providerType !== 'not_a_provider' ? (
                  <ProviderAvatar
                    userId={user.id}
                    initials={`${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}
                    discipline={user.providerType ?? ''}
                    size="md"
                    displayMode="badge"
                    badgeLabel={getDisciplineBadge(user.providerType)}
                    name={`${user.firstName} ${user.lastName}`}
                    photoUrl={user.photoUrl}
                    className="flex-shrink-0"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-slate-700 text-white flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  {rowBadges(user).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {rowBadges(user).map((badge) => (
                        <span key={badge.label} className={`rounded px-1.5 py-0.5 text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ),
          },
          {
            key: 'groups',
            label: 'Groups',
            width: 'w-48',
            render: (user: UserData) => (
              <div className="flex flex-wrap gap-1">
                {user.groups.map((group, idx) => {
                  const cfg = getGroupConfig(group);
                  return (
                    <span key={idx} className={`px-2 py-0.5 ${cfg.className}`}>
                      {cfg.label}
                    </span>
                  );
                })}
              </div>
            ),
          },
          {
            key: 'providerType',
            label: 'Provider Type',
            width: 'w-28',
            align: 'center' as const,
            render: (user: UserData) => (
              user.isProvider && user.providerType !== 'not_a_provider' ? (
                <DisciplinePill
                  discipline={getDisciplineBadge(user.providerType)}
                />
              ) : (
                <span className="text-gray-400">-</span>
              )
            ),
          },
          {
            key: 'lastLogin',
            label: 'Last login',
            width: 'w-40',
            render: (user: UserData) => (
              <span className="text-sm text-gray-600">{formatLastLogin(user.lastLoginAt)}</span>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            width: 'w-24',
            align: 'center' as const,
            render: (user: UserData) => (
              user.status ? (
                <StatusPill status={user.status} />
              ) : (
                <span className="text-gray-400">-</span>
              )
            ),
          },
        ]}
        actions={[
          {
            icon: <Edit size={14} />,
            label: 'Edit user',
            onClick: (user: UserData) => openEditModal(user),
            // A revoked user can only be invited again; editing would send a status the backend rejects
            isVisible: (user: UserData) => !isRevoked(user),
            className: 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
          },
          {
            icon: <Send size={14} />,
            label: 'Resend invite',
            onClick: (user: UserData) => handleResendInvite(user),
            isVisible: (user: UserData) => canInvite(user),
            disabled: (user: UserData) => inviteBusyId === user.id,
            className: 'p-1.5 text-sky-700 hover:text-sky-800 hover:bg-sky-50 rounded transition-colors',
          },
          {
            icon: <UserX size={14} />,
            label: 'Revoke invite',
            onClick: (user: UserData) => setRevokeTarget(user),
            isVisible: (user: UserData) => canInvite(user),
            disabled: (user: UserData) => inviteBusyId === user.id,
            className: 'p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors',
          },
        ]}
        // Mobile card config
        mobileCard={{
          avatar: (user: UserData) => (
            user.isProvider && user.providerType && user.providerType !== 'not_a_provider' ? (
              <ProviderAvatar
                userId={user.id}
                initials={`${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}
                discipline={user.providerType ?? ''}
                size="lg"
                displayMode="badge"
                badgeLabel={getDisciplineBadge(user.providerType)}
                name={`${user.firstName} ${user.lastName}`}
                photoUrl={user.photoUrl}
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-slate-700 text-white flex items-center justify-center">
                <span className="text-sm font-bold">{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
              </div>
            )
          ),
          primaryText: (user: UserData) => `${user.firstName} ${user.lastName}`,
          secondaryText: (user: UserData) => user.email,
          tertiaryText: (user: UserData) => `Last login: ${formatLastLogin(user.lastLoginAt)}`,
          badges: (user: UserData) => [
            ...rowBadges(user),
            ...user.groups.map(group => {
              const cfg = getGroupConfig(group);
              return { label: cfg.label, className: cfg.className };
            }),
            ...(user.isProvider && user.providerType && user.providerType !== 'not_a_provider' ? [{
              label: getDisciplineBadge(user.providerType),
              className: getDisciplinePillClassName(getDisciplineBadge(user.providerType)),
            }] : []),
            ...(user.status && !isRevoked(user) ? [{
              label: user.status.toUpperCase(),
              className: getStatusPillClassName(user.status),
            }] : []),
          ],
          actions: (user: UserData) => [
            ...(!isRevoked(user) ? [{
              icon: <Edit size={18} />,
              label: 'Edit',
              onClick: () => openEditModal(user),
            }] : []),
            ...(canInvite(user) ? [
              {
                icon: <Send size={18} />,
                label: 'Resend invite',
                onClick: () => handleResendInvite(user),
                disabled: inviteBusyId === user.id,
              },
              {
                icon: <UserX size={18} />,
                label: 'Revoke invite',
                onClick: () => setRevokeTarget(user),
                disabled: inviteBusyId === user.id,
              },
            ] : []),
          ],
        }}
        // Action bar props
        onRefresh={handleRefresh}
        refreshing={refreshing}
        loading={isLoading}
        newButtonLabel="Add User"
        newButtonDisabled={!canCreateUsersPii || isReadOnlyMode}
        onNewClick={() => {
          setSaveError(null);
          setProviderTypeError(null);
          setCurrentUser(null);
          setShowUserModal(true);
        }}
        // Pagination props
        // Pagination props
        pagination={{
          currentPage,
          totalPages: Math.ceil(totalCount / pageSize),
          pageSize,
          totalCount,
          onPageChange: setCurrentPage,
        }}
        // State props
        error={error}
        onDismissError={() => setError(null)}
        hasLoadedOnce={hasLoadedOnce}
        // Empty state props
        emptyStateIcon={<Users size={48} className="text-gray-300" />}
        emptyStateTitle="No users found"
        emptyStateMessage={searchTerm || filterGroup !== 'All' || filterProviderType !== 'All'
          ? 'Try adjusting your filters or search term.'
          : 'Get started by creating a new user.'}
        showList={!showUserModal}
        // Search and filter props
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search users by name, email, or NPI..."
        filters={filterConfigs}
        resultCount={filteredUsers.length}
        filterTotalCount={totalCount}
        filterStorageKey="userMgmt"
        desktopInlineFilters
        desktopFiltersCollapsedByDefault
        hideDesktopFilterResultCount
      />

      {/* User Modal */}
      {showUserModal && renderUserModal()}

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke invite?"
        message="The invite link stops working and the user is marked Revoked. You can invite them again later."
        confirmLabel="Revoke invite"
        destructive
        busy={revokeTarget !== null && inviteBusyId === revokeTarget.id}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </>
  );
};

export default UserManagement;